import {
  memo,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";

const LINE_CLAMP = (lines: number): CSSProperties => ({
  display: "-webkit-box",
  WebkitLineClamp: lines,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});

function getClampedOverflowText(
  element: HTMLElement,
  fullText: string,
  lineCount: number,
): string {
  const single = lineCount === 1;
  const fits = single
    ? element.scrollWidth <= element.clientWidth + 1 &&
      element.scrollHeight <= element.clientHeight + 1
    : element.scrollHeight <= element.clientHeight + 1;
  if (fits) return "";

  const computed = window.getComputedStyle(element);
  const measurer = document.createElement(single ? "span" : "div");
  measurer.style.cssText = single
    ? "position:absolute;visibility:hidden;pointer-events:none;white-space:nowrap"
    : `position:absolute;visibility:hidden;pointer-events:none;left:-9999px;top:0;width:${element.clientWidth}px;overflow:hidden;display:-webkit-box;-webkit-line-clamp:${lineCount};-webkit-box-orient:vertical`;
  measurer.style.font = computed.font;
  measurer.style.fontSize = computed.fontSize;
  measurer.style.fontWeight = computed.fontWeight;
  measurer.style.letterSpacing = computed.letterSpacing;
  if (!single) {
    measurer.style.lineHeight = computed.lineHeight;
    measurer.style.wordBreak = computed.wordBreak;
  }
  document.body.appendChild(measurer);

  let low = 0;
  let high = fullText.length;
  let visibleCount = 0;
  while (low <= high) {
    const mid = (low + high) >> 1;
    measurer.textContent = fullText.slice(0, mid);
    const ok = single
      ? measurer.offsetWidth <= element.clientWidth
      : measurer.scrollHeight <= measurer.clientHeight + 1;
    if (ok) {
      visibleCount = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }
  document.body.removeChild(measurer);
  return fullText.slice(visibleCount).trimStart();
}

export const ClampedTextWithTooltip = memo(function ClampedTextWithTooltip({
  id,
  text,
  className,
  style,
  lines = 2,
}: {
  id: string;
  text: string;
  className?: string;
  style?: CSSProperties;
  lines?: number;
}) {
  const textRef = useRef<HTMLDivElement>(null);
  const [overflowText, setOverflowText] = useState("");
  const [tooltipWidth, setTooltipWidth] = useState<number | null>(null);

  const measureOverflow = useCallback(() => {
    const el = textRef.current;
    if (!el || !text.trim()) {
      setOverflowText("");
      setTooltipWidth(null);
      return;
    }
    setTooltipWidth(el.closest("article")?.clientWidth ?? el.clientWidth);
    setOverflowText(getClampedOverflowText(el, text, lines));
  }, [text, lines]);

  useLayoutEffect(() => {
    measureOverflow();
    const el = textRef.current;
    if (!el) return;
    const card = el.closest("article");
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(el);
    if (card) observer.observe(card);
    return () => observer.disconnect();
  }, [measureOverflow]);

  const clampedText = (
    <div
      ref={textRef}
      className={`min-w-0 ${className ?? ""}`}
      style={{ ...LINE_CLAMP(lines), ...style }}
    >
      {text}
    </div>
  );

  if (!overflowText) return clampedText;

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Popover
          id={id}
          className="border shadow-sm"
          style={
            tooltipWidth
              ? { width: tooltipWidth, maxWidth: tooltipWidth }
              : undefined
          }
        >
          <Popover.Body className="small text-start text-dark py-2 px-3">
            {overflowText}
          </Popover.Body>
        </Popover>
      }
    >
      {clampedText}
    </OverlayTrigger>
  );
});
